import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { toast } from "react-toastify";
import { createCashRegister, deleteCashRegister, fetchCashRegisters, updateCashRegister } from "../../services/cashService";
import styles from "../shifts/shifts.module.scss";

const AdminCashRegisters = () => {
    const history = useHistory();
    const dispatch = useDispatch();
    const [registers, setRegisters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingRegister, setEditingRegister] = useState(null);
    const [name, setName] = useState("");
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const loadRegisters = async () => {
        setLoading(true);
        const res = await fetchCashRegisters();
        setRegisters(res?.data || []);
        setLoading(false);
    };

    useEffect(() => {
        loadRegisters();
    }, []);

    const openAddDialog = () => {
        setEditingRegister(null);
        setName("");
        setDialogVisible(true);
    };

    const openEditDialog = (e, register) => {
        e.stopPropagation();
        setEditingRegister(register);
        setName(register.name);
        setDialogVisible(true);
    };

    const handleSave = async () => {
        if (!name.trim()) {
            toast.error("Register name is required");
            return;
        }
        setSaving(true);
        try {
            const res = editingRegister ? await updateCashRegister(editingRegister._id, name.trim()) : await createCashRegister(dispatch, name.trim());
            if (res?.success) {
                toast.success(editingRegister ? "Register updated" : "Register created");
                setDialogVisible(false);
                loadRegisters();
            }
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (e, register) => {
        e.stopPropagation();
        confirmDialog({
            message: `Delete ${register.name}? Past shifts and cash entries stay linked to it, but it won't be available to pick for new shifts.`,
            header: "Delete Register",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            accept: async () => {
                setDeletingId(register._id);
                try {
                    const res = await deleteCashRegister(dispatch, register._id);
                    if (res?.success) {
                        toast.success("Register deleted");
                        loadRegisters();
                    }
                } finally {
                    setDeletingId(null);
                }
            },
        });
    };

    return (
        <div className={styles.page}>
            <ConfirmDialog />

            <div className={styles.pageHeader}>
                <div className={styles.headerTitleRow}>
                    <div className={styles.headerIcon}>
                        <i className="pi pi-inbox" />
                    </div>
                    <div>
                        <h2>Cash Registers</h2>
                        <p className={styles.subtitle}>Physical tills - one active or pending shift per register at a time</p>
                    </div>
                </div>
                <Button label="Add Register" icon="pi pi-plus" className={styles.primaryButton} onClick={openAddDialog} />
            </div>

            {loading ? (
                <div className={styles.loadingState}>
                    <ProgressSpinner strokeWidth="4" />
                    <span>Loading registers...</span>
                </div>
            ) : registers.length === 0 ? (
                <div className={styles.sectionCard}>
                    <div className={styles.emptyBreaks}>No cash registers yet.</div>
                </div>
            ) : (
                <div className={styles.sectionCard}>
                    {registers.map((r) => (
                        <div key={r._id} className={styles.listRow} style={{ cursor: "pointer" }} onClick={() => history.push(`/admin/cash-registers/${r._id}`)}>
                            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                                <strong>{r.name}</strong>
                                <span>{r.inUseBy ? `In use by ${r.inUseBy}` : "Free"}</span>
                            </div>
                            <span style={{ display: "flex", gap: "4px" }}>
                                <Button icon="pi pi-pencil" className="p-button-text p-button-sm" onClick={(e) => openEditDialog(e, r)} tooltip="Edit register" />
                                <Button
                                    icon={deletingId === r._id ? "pi pi-spin pi-spinner" : "pi pi-trash"}
                                    className="p-button-text p-button-danger p-button-sm"
                                    onClick={(e) => handleDelete(e, r)}
                                    disabled={!!r.inUseBy || deletingId === r._id}
                                    tooltip={r.inUseBy ? "In use - release it before deleting" : "Delete register"}
                                />
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <Dialog header={editingRegister ? "Edit Register" : "Add Register"} visible={dialogVisible} style={{ width: "min(420px, 92vw)" }} onHide={() => setDialogVisible(false)}>
                <div style={{ display: "flex", flexDirection: "column", gap: "14px", marginTop: "6px" }}>
                    <div>
                        <label style={{ display: "block", fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px" }}>Register Name</label>
                        <InputText value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Richmond 2" style={{ width: "100%" }} />
                    </div>
                    <Button
                        label={saving ? "Saving..." : editingRegister ? "Save Changes" : "Create Register"}
                        icon={saving ? "pi pi-spin pi-spinner" : "pi pi-check"}
                        className={styles.primaryButton}
                        onClick={handleSave}
                        disabled={saving}
                    />
                </div>
            </Dialog>
        </div>
    );
};

export default AdminCashRegisters;
